/* eslint-disable no-console */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { useQueryClient } from "@tanstack/react-query";
// components
import SearchScreen from "../../components/screens/Search/SearchScreen";
import {
  courses as courseQuerys,
  search as searchQuerys,
} from "@nualang/nualang-api-and-queries/Queries";
import { searchAllTypes } from "@nualang/nualang-api-and-queries/APIs/Search";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import NoCoursesImage from "../../img/noCourses.svg";
import MembersImage from "../../img/members.svg";
// config
import config from "../../config";
import { randomId } from "../../utils/index";
import { getCourseSections } from "@nualang/nualang-api-and-queries/APIs/CourseSections";
import {
  getCourseMember,
  getCourseMembers,
  createCourseMember,
  deleteCourseMember,
} from "@nualang/nualang-api-and-queries/APIs/CourseMembers";
import { courses as courseHelpers, users as usersHelpers } from "../../utils/helpers";
import { difficulties } from "../../utils/index";
import { useLocation } from "react-router";
import { useApp, useCourse } from "../../hooks";
import { flattenSearchPages, getNextSearchPageParam } from "./searchResults";

const { languages, forLanguages } = config;

function Search(props) {
  // const username = 12345
  const {
    authData: { username },
    selectable,
    navigate,
    classroomId,
    authenticated,
    handleSignIn,
    learnLang = "",
    forLang = "",
    disablePadding,
    isLearnLangDisabled,
    scrollTarget,
  } = props;
  const queryClient = useQueryClient();

  const location = useLocation();

  const {
    selectedCourses,
    setSelectedCourses,
    handleClickCourse,
    setNewCourse,
    setIsJoinCourseDialogOpen,
  } = useCourse();
  const { user, openSnackbar, startLoading, stopLoading } = useApp();
  const userAttr = user.attributes !== undefined ? user.attributes : null;

  const { t } = useTranslation();
  const queryParams = new URLSearchParams(location.search);
  const sortType = queryParams.get("sort") || "";
  const text = queryParams.get("searchText") || "";
  // Searches rank by relevance unless the URL picks a sort; browsing without
  // text always shows the most popular first.
  const sortFromUrl = text ? sortType || "relevance" : "popular";

  const [search, setSearch] = useState("");
  // Start from the URL's search and sort so the first render fires the one
  // correct search — the query key is the whole filters object, so patching
  // these in afterwards would run a full multi-type fan-out per change.
  const [filters, setFilters] = useState({
    difficulty: "all",
    learnLang: learnLang
      ? learnLang
      : userAttr
        ? userAttr["custom:learnLang"]
        : "",
    forLang: forLang ? forLang : userAttr ? userAttr["custom:forLang"] : "",
    orderBy: "",
    // Fetched per type (courses/topics/chatbots/scenarios), so the "All" tab's
    // first page is up to 4× this.
    limit: 12,
    visibility: "public",
    returnvalues: "",
    lastEvaluatedKey: "",
    lastEvaluatedCourseId: "",
    lastEvaluatedCreatedAt: "",
    lastEvaluatedExerciseStartTotal: "",
    sort: sortFromUrl,
    search: text,
    verbose: false,
    page: 1,
  });

  const { orderBy } = filters;

  const forLangFilter = filters.forLang;
  const learnLangFilter = filters.learnLang;

  const [sortByFilter, setSortByFilter] = useState(sortFromUrl);
  // Selected result types (multi-select), kept in the URL as ?types=topic,chatbot
  // so the selection survives a refresh, the back button and shared links.
  const KNOWN_TYPES = ["course", "topic", "chatbot", "scenario"];
  const selectedTypes = (queryParams.get("types") || "")
    .split(",")
    .map((type) => type.trim())
    .filter((type) => KNOWN_TYPES.includes(type));

  // A new header search (or sort in the URL) while on this page: update the
  // search text and sort together so it costs a single fetch.
  useEffect(() => {
    setSortByFilter(sortFromUrl);
    setFilters((prevFilters) =>
      prevFilters.search === text && prevFilters.sort === sortFromUrl
        ? prevFilters
        : { ...prevFilters, search: text, sort: sortFromUrl, page: 1 },
    );
  }, [text, sortFromUrl]);

  const { ref, inView } = useInView();

  // Fan out across courses/topics/chatbots/scenarios, then reuse the existing
  // course formatting (avatar, verification, card images) on every result.
  const fetchSearch = useCallback(
    async (searchFilters) => {
      // Pass the viewer so items in private courses they can't reach are hidden
      // (the creator still sees their own).
      const page = await searchAllTypes({
        ...searchFilters,
        username: props.authData?.username,
      });
      const Items = await courseHelpers.formatCourses(
        page.Items,
        props.authData,
        { useResolvedCreator: true },
      );
      return { ...page, Items };
    },
    [props.authData],
  );

  const {
    data = {},
    isLoading,
    isError,
    isFetchingNextPage,
    fetchNextPage,
  } = searchQuerys.useMixedSearch(
    fetchSearch,
    { filters },
    {
      enabled: !(filters.sort === "relevance" && text === ""),
      staleTime: 0,
      getNextPageParam: (lastPage, allPages) =>
        getNextSearchPageParam(lastPage, allPages, filters.limit),
    },
  );

  useEffect(() => {
    if (isError) {
      openSnackbar("search_error", "error");
    }
  }, [isError]);

  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage]);

  const allItems = useMemo(
    () => flattenSearchPages(data.pages),
    [data, text],
  );

  // Per-type chip counts are the search service's totals for the current
  // query + filters (the same on every page), so they don't grow as more
  // results load. `null` = total unknown; the chip then shows no number.
  const typeCounts = data.pages?.[0]?.counts || {};

  // Empty selection means "All"; otherwise show the union of selected types.
  const visibleItems = useMemo(
    () =>
      selectedTypes.length === 0
        ? allItems
        : allItems.filter((item) =>
            selectedTypes.includes(item?.type || "course"),
          ),
    [allItems, selectedTypes],
  );

  const handleTypesChange = (nextTypes) => {
    const params = new URLSearchParams(location.search);
    if (nextTypes && nextTypes.length > 0) {
      params.set("types", nextTypes.join(","));
    } else {
      params.delete("types");
    }
    navigate(`/search?${params.toString()}`);
    if (window.scrollY > 100) {
      window.scrollTo(0, 0);
    }
  };
  const handleFilterChange = (event) => {
    const { target } = event;
    const value = target.type === "checkbox" ? target.checked : target.value;
    const { name } = target;

    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
      page: 1,
      lastEvaluatedKey: "",
      lastEvaluatedCourseId: "",
      lastEvaluatedCreatedAt: "",
      lastEvaluatedExerciseStartTotal: "",
    }));
    if (name === "sort") {
      setSortByFilter(value);
    } else if (name === "search") {
      setSearch(value);
    }

    if (window.scrollY > 100) {
      window.scrollTo(0, 0);
    }
  };

  // Mutations
  const joinCourseMutation = courseQuerys.useJoinCourse(createCourseMember, {
    onSettled: () => {
      // Error or success... doesn't matter!
      stopLoading();
    },
    onSuccess: async (data, variables) => {
      openSnackbar("joined_course", "success");
      navigate(`/courses/${variables.courseId}`);
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMembers(
          variables.courseId,
          username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMember(
          variables.courseId,
          username,
          username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.list({
          memberId: username,
          verbose: false,
        }),
      });
    },
    onError: () => {
      openSnackbar("problem", "error");
    },
  });
  const leaveCourseMutation = courseQuerys.useLeaveCourse(deleteCourseMember, {
    onSettled: () => {
      // Error or success... doesn't matter!
      stopLoading();
    },
    onSuccess: async (data, variables) => {
      openSnackbar("left_course", "success");
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMembers(
          variables.courseId,
          username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMember(
          variables.courseId,
          username,
          username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.list({
          memberId: username,
          verbose: false,
        }),
      });
    },
    onError: () => {
      openSnackbar("problem", "error");
    },
  });

  const orderKeys = ["popularity"];
  const orderOptions = orderKeys.map((key, idx) => (
    <MenuItem key={`ordercousear_${idx}${randomId()}`} value={key}>
      {t(key)}
    </MenuItem>
  ));

  const handleStartCourse = async (course) => {
    if (!authenticated) {
      handleSignIn();
    } else {
      setNewCourse(course);
      if (course.visibility === "private") {
        setIsJoinCourseDialogOpen(true);
      } else {
        startLoading();
        joinCourseMutation.mutate({ courseId: course.courseId, username });
      }
    }
  };

  const handleLeaveCourse = (courseId) => {
    startLoading();
    leaveCourseMutation.mutate({ courseId, username });
  };

  const difficultyOptions = difficulties.map((d, idx) => (
    <MenuItem key={`diffcsearrole_${idx}`} value={d}>
      {t(d)}
    </MenuItem>
  ));

  const handleViewCourse = (courseId) => navigate(`/courses/${courseId}`);
  const handleCreateCourse = () =>
    navigate(
      `/courses/create/${classroomId ? `?classroomId=${classroomId}` : ""}`,
    );
  const getMemberDetails = (members) => usersHelpers.fetchMembersDetails(members);

  const handleViewActivity = (memberId) => {
    navigate(`/activity/member/${memberId}`);
  };

  const handleViewCourseTopic = (cId, sectionId, topicId) => {
    navigate(`/courses/${cId}/${sectionId}/${topicId}`);
  };

  // "From course" link on non-course result cards → the parent course page.
  const handleViewParentCourse = (courseId) => navigate(`/courses/${courseId}`);

  // "View topic / chatbot / scenario" — all open the parent topic page. For a
  // chatbot/scenario we pass ?exercise=… so the topic scrolls to that exercise.
  const handleStartItem = (item) => {
    if (!item) {
      return;
    }
    if (item.type === "topic") {
      const [cId, sId] = (item.courseSectionId || "").split("|");
      if (cId && sId && item.topicId) {
        navigate(`/courses/${cId}/${sId}/${item.topicId}`);
        return;
      }
    } else {
      const [cId, sId, tId] = (item.courseSectionTopicId || "").split("|");
      if (item.type === "chatbot" && cId && sId && tId && item.botId) {
        navigate(`/courses/${cId}/${sId}/${tId}?exercise=bot-${item.botId}`);
        return;
      }
      if (item.type === "scenario" && cId && sId && tId && item.roleplayId) {
        navigate(
          `/courses/${cId}/${sId}/${tId}?exercise=roleplay-${item.roleplayId}`,
        );
        return;
      }
    }
    // Fall back to the parent course when the item isn't attached to a topic.
    if (item.parentCourse?.id) {
      navigate(`/courses/${item.parentCourse.id}`);
    }
  };

  const handleViewUserProfile = (profileId) => {
    navigate(`/profiles/${profileId}`);
  };

  const handleSelectAllCoursesClick = (event) => {
    if (event.target.checked) {
      const newSelectedCourses = allItems.map((c) => c.courseId);
      setSelectedCourses(newSelectedCourses);
      return;
    }
    setSelectedCourses([]);
  };
  return (
    <React.Fragment>
      <SearchScreen
        t={t}
        isLoading={isLoading}
        placeholderImageUrl={NoCoursesImage}
        memberPlaceholderImageUrl={MembersImage}
        courses={visibleItems || []}
        selectedTypes={selectedTypes}
        typeCounts={typeCounts}
        handleTypesChange={handleTypesChange}
        query={text}
        showTypeBadge
        handleStartCourse={handleStartCourse}
        handleStartItem={handleStartItem}
        handleViewParentCourse={handleViewParentCourse}
        handleLeaveCourse={handleLeaveCourse}
        handleViewCourse={handleViewCourse}
        learnLang={learnLangFilter}
        forLang={forLangFilter}
        sort={sortByFilter}
        orderBy={orderBy}
        handleViewCourseTopic={handleViewCourseTopic}
        languages={languages}
        forLanguages={forLanguages}
        orderOptions={orderOptions}
        handleFilterChange={handleFilterChange}
        handleCreateCourse={handleCreateCourse}
        selectable={selectable}
        selectedCourses={selectedCourses}
        handleClickCourse={handleClickCourse}
        handleSelectAllCoursesClick={handleSelectAllCoursesClick}
        getMemberDetails={getMemberDetails}
        getCourseSections={getCourseSections}
        memberId={username}
        getCourseMember={getCourseMember}
        getCourseMembers={getCourseMembers}
        username={username}
        membersProps={{
          handleViewActivity,
        }}
        disablePadding={disablePadding}
        handleViewUserProfile={handleViewUserProfile}
        isLearnLangDisabled={isLearnLangDisabled}
        scrollTarget={scrollTarget}
        search={search}
        setSearch={setSearch}
        difficulty={filters.difficulty}
        difficultyOptions={difficultyOptions}
      />
      <Box
        ref={ref}
        sx={{
          textAlign: "center",
          mt: 1
        }}>
        {/* Only spin while actually loading the next page — never as an idle
            "there might be more" indicator. */}
        {isFetchingNextPage ? <CircularProgress /> : ""}
      </Box>
    </React.Fragment>
  );
}

Search.propTypes = {
  authData: PropTypes.object,
  navigate: PropTypes.func,
  scrollableTarget: PropTypes.string,
  selectable: PropTypes.bool,
  learnLang: PropTypes.string,
  forLang: PropTypes.string,
  disablePadding: PropTypes.bool,
};

export default Search;
